import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ChatMessageProps {
  role: string;
  content: string;
  documentPath?: string;
  citationTexts?: string[];
  documentQuotes?: string[];
  isTyping?: boolean;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ role, content, documentQuotes, isTyping }) => {
  const isUser = role === 'user';
  const isTypingIndicator = isTyping || (!isUser && content === '...');

  // Custom typing indicator component
  const TypingIndicator = () => (
    <div className="flex space-x-2 items-center py-1">
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
    </div>
  );

  // Minimal preprocessing - only remove quotes section
  const preprocessContent = (text: string): string => {
    if (!text) return '';

    // Remove quotes section from original content (will be displayed separately)
    const quotesRegex = /<quotes>([\s\S]*?)<\/quotes>/;
    return text.replace(quotesRegex, '');
  };

  // Extract quotes from content for separate display
  const extractQuotes = (text: string): string[] => {
    if (!text) return [];

    const quotes: string[] = [];

    // First try to extract from explicit quotes section
    const quotesRegex = /<quotes>([\s\S]*?)<\/quotes>/;
    const quotesMatch = text.match(quotesRegex);

    if (quotesMatch) {
      // Found a quotes section
      console.log('Found quotes section in message');
      const quotesContent = quotesMatch[1];

      // Support both formats: with ID or without ID
      const individualQuoteRegex = /<quote(?:\s+id=(\d+))?>([\s\S]*?)<\/quote>/g;
      let match;

      while ((match = individualQuoteRegex.exec(quotesContent)) !== null) {
        // match[2] is the quote content because match[1] is the optional ID
        const quoteText = match[2].trim();
        if (quoteText) {
          quotes.push(quoteText);
          console.log(`Extracted quote: ${quoteText.substring(0, 50)}...`);
        }
      }
    } else {
      // No quotes section, check for fallback quotes
      console.log('No quotes section found in message, checking for fallback');

      // Look for fallback notice that indicates forced quoting
      if (text.includes("System has automatically highlighted relevant sections")) {
        console.log('Found fallback quote notice');
      }
    }

    return quotes;
  };

  // Extract quotes from the content directly
  const extractedQuotes = extractQuotes(content);

  // Use provided documentQuotes if available, otherwise fall back to
  // quotes extracted directly from the message content
  const quotes = (documentQuotes && documentQuotes.length > 0) ?
    documentQuotes : extractedQuotes;

  // Log quote information for debugging
  if (!isUser) {
    if (documentQuotes && documentQuotes.length > 0) {
      console.log(`Using ${documentQuotes.length} quotes from props`);
    } else if (extractedQuotes.length > 0) {
      console.log(`Using ${extractedQuotes.length} quotes extracted from message content`);
    } else {
      console.log('No quotes available from props or content');
    }
  }

  // CRITICAL: Use this special data attribute to check if we've already added quotes to this message
  // This ensures quotes don't disappear on re-renders
  const [hasProcessedQuotes, setHasProcessedQuotes] = React.useState(false);
  const [permanentQuotes, setPermanentQuotes] = React.useState<string[]>([]);

  // On first render, store the quotes permanently with this message
  React.useEffect(() => {
    if (!hasProcessedQuotes && quotes.length > 0) {
      console.log('Storing permanent quotes for this message:', quotes.length);
      setPermanentQuotes(quotes);
      setHasProcessedQuotes(true);
    }
  }, [quotes, hasProcessedQuotes]);

  // Use our permanent quotes if available, otherwise fall back to original quotes
  const finalQuotes = hasProcessedQuotes ? permanentQuotes : quotes;

  // Don't render empty messages
  if (!content || content.trim() === '') {
    return null;
  }

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`max-w-[80%] rounded-[18px] px-5 py-4 ${
        isUser
          ? 'bg-gray-100'
          : 'bg-white shadow-sm'
      }`}>

        {/* Show typing indicator if this is a '...' message from assistant */}
        {isTypingIndicator ? (
          <TypingIndicator />
        ) : (
          <>
            {/* Main message content */}
            <div className="text-sm text-gray-900 markdown">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  // Paragraphs
                  p: ({node, ...props}) => <p className="my-2" {...props} />,

                  // Clean list styling
                  li: ({node, ...props}) => <li className="my-1" {...props} />,
                  ul: ({node, ...props}) => <ul className="my-2 ml-4 list-disc" {...props} />,
                  ol: ({node, ...props}) => <ol className="my-2 ml-4 list-decimal" {...props} />,

                  // Headings
                  h1: ({node, ...props}) => <h1 className="text-xl font-bold my-3" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-lg font-bold my-2" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-md font-bold my-2" {...props} />,
                  h4: ({node, ...props}) => <h4 className="font-bold my-2" {...props} />,
                  h5: ({node, ...props}) => <h5 className="font-bold my-2" {...props} />,
                  h6: ({node, ...props}) => <h6 className="font-bold my-2" {...props} />,

                  // Emphasis and styling
                  strong: ({node, ...props}) => <strong className="font-semibold" {...props} />,
                  em: ({node, ...props}) => <em className="italic" {...props} />,

                  // Code blocks
                  code: ({inline, className, children, ...props}: any) => {
                    const match = /language-(\w+)/.exec(className || '');
                    return inline ? (
                      <code className="font-mono px-1 py-0.5 bg-gray-100 rounded text-sm" {...props}>{children}</code>
                    ) : (
                      <code className={`font-mono block p-3 my-2 bg-gray-50 rounded text-sm ${match ? `language-${match[1]}` : ''}`} {...props}>
                        {children}
                      </code>
                    );
                  },
                  pre: ({node, ...props}) => <pre className="my-2 overflow-x-auto" {...props} />,

                  // Quotes and links
                  blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-gray-300 pl-4 my-2 italic text-gray-600" {...props} />,
                  a: ({node, ...props}) => <a className="text-blue-600 underline hover:text-blue-700" target="_blank" rel="noopener noreferrer" {...props} />,

                  // Media
                  img: ({node, ...props}) => <img className="max-w-full h-auto my-2 rounded" {...props} />,

                  // Tables - enhanced styling for better readability
                  table: ({node, ...props}) => (
                    <div className="overflow-x-auto my-4">
                      <table className="min-w-full border-collapse border border-gray-300 bg-white rounded-lg" {...props} />
                    </div>
                  ),
                  thead: ({node, ...props}) => <thead className="bg-gray-100" {...props} />,
                  th: ({node, ...props}) => (
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700 bg-gray-50" {...props} />
                  ),
                  td: ({node, ...props}) => (
                    <td className="border border-gray-300 px-4 py-3 text-gray-900" {...props} />
                  ),
                  tr: ({node, ...props}) => <tr className="hover:bg-gray-50" {...props} />
                }}
              >
                {preprocessContent(content)}
              </ReactMarkdown>
            </div>

            {/* Only display quotes section for assistant messages when there are quotes to show,
                and don't show for "✅ Yes" responses */}
            {!isUser &&
             content !== "How can I assist you today?" &&
             !content.trim().startsWith("✅ Yes") &&
             finalQuotes.length > 0 && (
              <div className="mt-4 border-t border-gray-200 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-gray-700 text-sm">
                    Requires human review:
                  </p>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                    {finalQuotes.length} quote{finalQuotes.length > 1 ? 's' : ''}
                  </span>
                </div>

                <div className="space-y-2">
                  {finalQuotes.map((quote, index) => (
                    <div key={index} className="bg-amber-50 p-3 rounded-md border-l-4 border-amber-400 transition-all hover:bg-amber-100">
                      <p className="text-sm text-gray-700 pl-1">
                        <span className="inline-block w-4 text-amber-600 align-top">•</span>
                        <span>"{quote.length > 250 ? `${quote.substring(0, 250)}...` : quote}"</span>
                      </p>
                      {quote.length > 250 && (
                        <button
                          onClick={() => alert(`Full quote: "${quote}"`)}
                          className="mt-1 ml-5 text-xs text-amber-600 underline hover:text-amber-700"
                        >
                          Show full quote
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;